import { PROTOCOL_SERVER_GENERATION, PROTOCOL_VERSION } from "@rivets/core";
import { useConnectionStatus } from "./connectionState";

/**
 * Diagnostics (CONTRACT "Diagnostics"): what went wrong in this page, kept in
 * memory for "Copy diagnostics" in the account menu.
 *
 * Uncaught errors and unhandled rejections, render crashes the error
 * boundaries caught, and frames or events the socket could not read all land
 * in one ring of the last 50. Nothing is sent anywhere.
 */

const RING_LIMIT = 50;
const ring: string[] = [];

export function recordDiagnostic(kind: string, detail: unknown): void {
  const line = `${new Date().toISOString()} [${kind}] ${describe(detail)}`;
  ring.push(line.length > 2_000 ? `${line.slice(0, 2_000)}…` : line);
  if (ring.length > RING_LIMIT) ring.splice(0, ring.length - RING_LIMIT);
}

export function recentDiagnostics(): readonly string[] {
  return ring;
}

/** Test seam. */
export function clearDiagnostics(): void {
  ring.length = 0;
}

function describe(detail: unknown): string {
  if (detail instanceof Error) return `${detail.name}: ${detail.message}${detail.stack ? `\n${detail.stack.split("\n").slice(1, 6).join("\n")}` : ""}`;
  if (typeof detail === "string") return detail;
  try { return JSON.stringify(detail); } catch { return String(detail); }
}

let installed = false;

/** The page-wide `error` and `unhandledrejection` handlers. Once per page. */
export function installGlobalErrorHandlers(target: Pick<Window, "addEventListener"> | undefined = typeof window === "undefined" ? undefined : window): void {
  if (installed || !target) return;
  installed = true;
  target.addEventListener("error", (event) => {
    const error = (event as ErrorEvent).error ?? (event as ErrorEvent).message;
    recordDiagnostic("error", error);
  });
  target.addEventListener("unhandledrejection", (event) => {
    recordDiagnostic("unhandledrejection", (event as PromiseRejectionEvent).reason);
  });
}

/** Sections other layers add (the socket's protocol and chat sync). */
const sections = new Map<string, () => string>();
export function registerDiagnosticSection(name: string, read: () => string): void {
  sections.set(name, read);
}

/** The report "Copy diagnostics" puts on the clipboard. */
export function diagnosticsReport(now = new Date()): string {
  const connection = useConnectionStatus.getState();
  const parts = [
    [
      `Rivets web — protocol ${PROTOCOL_VERSION} (${PROTOCOL_SERVER_GENERATION})`,
      typeof navigator === "undefined" ? "" : navigator.userAgent,
      `Page: ${typeof location === "undefined" ? "?" : location.pathname}`,
      `Generated ${now.toISOString()}`,
    ].filter(Boolean).join("\n"),
    [
      "## Connection",
      `Now: ${connection.actual} (shown: ${connection.presented})`,
      ...connection.history,
    ].join("\n"),
    ...[...sections].map(([name, read]) => {
      let body: string;
      try { body = read(); } catch (error) { body = `(unavailable: ${describe(error)})`; }
      return `## ${name}\n${body}`;
    }),
    ["## Recent errors (last 50)", ...(ring.length > 0 ? ring : ["none"])].join("\n"),
  ];
  return parts.join("\n\n");
}

/** The native `AppModel.diagnosticsCopiedNotice`. */
export const DIAGNOSTICS_COPIED_NOTICE = "Diagnostics copied to the clipboard.";

export async function copyDiagnostics(): Promise<boolean> {
  const text = diagnosticsReport();
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
